import React, { useEffect, useState, useRef, Component } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useInView, useScroll, useTransform } from 'framer-motion';
import { BarChart3Icon, TrendingUpIcon, ShieldCheckIcon, ZapIcon, UsersIcon, LineChartIcon, CheckCircleIcon, ArrowRightIcon, SparklesIcon, ClockIcon, TargetIcon, MenuIcon, XIcon } from 'lucide-react';
const Landing = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const features = [{
    icon: <BarChart3Icon size={24} data-id="element-1574" />,
    title: 'Reportes en tiempo real',
    description: 'Estado de resultados, flujo de efectivo y balance general actualizados automáticamente.'
  }, {
    icon: <TrendingUpIcon size={24} data-id="element-1575" />,
    title: 'Análisis predictivo',
    description: 'Proyecciones financieras basadas en IA para anticipar escenarios futuros.'
  }, {
    icon: <ShieldCheckIcon size={24} data-id="element-1576" />,
    title: 'Seguridad bancaria',
    description: 'Encriptación de nivel bancario y cumplimiento con estándares internacionales.'
  }, {
    icon: <ZapIcon size={24} data-id="element-1577" />,
    title: 'Automatización inteligente',
    description: 'Reduce el trabajo manual con procesos automatizados y alertas proactivas.'
  }, {
    icon: <UsersIcon size={24} data-id="element-1578" />,
    title: 'Colaboración en equipo',
    description: 'Comparte insights con tu equipo y toma decisiones basadas en datos.'
  }, {
    icon: <LineChartIcon size={24} data-id="element-1579" />,
    title: 'KPIs personalizados',
    description: 'Define y monitorea los indicadores que realmente importan para tu negocio.'
  }];
  const benefits = [{
    icon: <ClockIcon size={32} data-id="element-1580" />,
    title: 'Ahorra 20+ horas al mes',
    description: 'Automatiza reportes y análisis que antes tomaban días en preparar.'
  }, {
    icon: <TargetIcon size={32} data-id="element-1581" />,
    title: 'Decisiones más inteligentes',
    description: 'Accede a insights accionables que impulsan el crecimiento de tu empresa.'
  }, {
    icon: <SparklesIcon size={32} data-id="element-1582" />,
    title: 'Implementación en 24 horas',
    description: 'Conecta tu sistema contable y empieza a ver resultados de inmediato.'
  }];
  const testimonials = [{
    name: 'Ana Beltrán',
    role: 'CEO, PYME Solutions',
    image: 'AB',
    content: 'FinancePro transformó completamente nuestra gestión financiera. Ahora tomamos decisiones basadas en datos en tiempo real.',
    company: 'PYME Solutions'
  }, {
    name: 'Carlos Mendoza',
    role: 'CFO, TechStart',
    image: 'CM',
    content: 'La mejor inversión que hemos hecho. Recuperamos el costo en el primer mes solo por el tiempo ahorrado.',
    company: 'TechStart'
  }, {
    name: 'Laura Torres',
    role: 'Directora Financiera, GrowthCo',
    image: 'LT',
    content: 'Los insights automáticos nos ayudaron a identificar oportunidades de ahorro que no habíamos visto antes.',
    company: 'GrowthCo'
  }];
  const pricingPlans = [{
    name: 'Starter',
    price: '2499',
    description: 'Perfecto para empresas en crecimiento',
    features: ['1 usuario', 'Reportes básicos', 'Actualizaciones manuales', '1 Asesoría Financiera Mensual', 'Almacenamiento 5GB'],
    cta: 'Empezar prueba',
    popular: false
  }, {
    name: 'Profesional',
    price: '6,499',
    description: 'Para empresas establecidas',
    features: ['Hasta 5 usuarios', 'Reportes avanzados', 'Actualizaciones en tiempo real', 'Chat ilimitado con expertos', 'Soporte prioritario', 'Análisis predictivo', 'API access'],
    cta: 'Empezar prueba',
    popular: true
  }, {
    name: 'CFO Dedicado',
    price: 'Personalizado',
    description: 'Solución a la medida',
    features: ['Usuarios ilimitados', 'Reportes personalizados', 'Financiero dedicado', 'Tesorería a la medida', 'Análisis financiero a la medida', 'Colaboración con equipo interno', 'IA personalizada', 'Integraciones custom', 'Onboarding dedicado'],
    cta: 'Contactar ventas',
    popular: false
  }];
  const faqs = [{
    question: '¿Qué sistemas contables se integran con FinancePro?',
    answer: 'FinancePro se integra con QuickBooks, Xero, SAP, y más de 50 sistemas contables populares. Si tu sistema no está listado, contáctanos para una integración personalizada.'
  }, {
    question: '¿Cuánto tiempo toma la implementación?',
    answer: 'La mayoría de nuestros clientes están operando en menos de 24 horas. Nuestro equipo te guía en cada paso del proceso de integración.'
  }, {
    question: '¿Mis datos están seguros?',
    answer: 'Absolutamente. Usamos encriptación de nivel bancario (AES-256), cumplimos con SOC 2 Type II, y tus datos nunca se comparten con terceros.'
  }, {
    question: '¿Puedo cancelar en cualquier momento?',
    answer: 'Sí, puedes cancelar tu suscripción en cualquier momento sin penalizaciones. Mantendrás acceso hasta el final de tu período de facturación.'
  }, {
    question: '¿Ofrecen capacitación para mi equipo?',
    answer: 'Sí, todos los planes incluyen capacitación inicial. Los planes Enterprise incluyen capacitación continua y un gerente de cuenta dedicado.'
  }];
  return <div className="min-h-screen bg-white" data-id="element-1583">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-white/80 backdrop-blur-md border-b border-gray-200 z-50" data-id="element-1584">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1585">
          <div className="flex justify-between items-center h-16" data-id="element-1586">
            <div className="flex items-center" data-id="element-1587">
              <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent" data-id="element-1588">
                FinancePro
              </h1>
            </div>
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8" data-id="element-1589">
              <a href="#features" className="text-gray-700 hover:text-blue-600 transition-colors" data-id="element-1590">
                Características
              </a>
              <a href="#pricing" className="text-gray-700 hover:text-blue-600 transition-colors" data-id="element-1591">
                Precios
              </a>
              <a href="#testimonials" className="text-gray-700 hover:text-blue-600 transition-colors" data-id="element-1592">
                Testimonios
              </a>
              <button onClick={() => navigate('/login')} className="text-gray-700 hover:text-blue-600 transition-colors" data-id="element-1593">
                Iniciar sesión
              </button>
              <button onClick={() => navigate('/register')} className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors" data-id="element-1594">
                Prueba gratis
              </button>
            </div>
            {/* Mobile menu button */}
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden p-2" data-id="element-1595">
              {mobileMenuOpen ? <XIcon size={24} data-id="element-1596" /> : <MenuIcon size={24} data-id="element-1597" />}
            </button>
          </div>
        </div>
        {/* Mobile Navigation */}
        {mobileMenuOpen && <motion.div initial={{
        opacity: 0,
        y: -20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="md:hidden border-t border-gray-200 bg-white" data-id="element-1598">
            <div className="px-4 py-4 space-y-3" data-id="element-1599">
              <a href="#features" className="block text-gray-700 hover:text-blue-600 transition-colors" onClick={() => setMobileMenuOpen(false)} data-id="element-1600">
                Características
              </a>
              <a href="#pricing" className="block text-gray-700 hover:text-blue-600 transition-colors" onClick={() => setMobileMenuOpen(false)} data-id="element-1601">
                Precios
              </a>
              <a href="#testimonials" className="block text-gray-700 hover:text-blue-600 transition-colors" onClick={() => setMobileMenuOpen(false)} data-id="element-1602">
                Testimonios
              </a>
              <button onClick={() => navigate('/login')} className="block w-full text-left text-gray-700 hover:text-blue-600 transition-colors" data-id="element-1603">
                Iniciar sesión
              </button>
              <button onClick={() => navigate('/register')} className="block w-full bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors text-center" data-id="element-1604">
                Prueba gratis
              </button>
            </div>
          </motion.div>}
      </nav>
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-blue-50 to-white" data-id="element-1605">
        <div className="max-w-7xl mx-auto" data-id="element-1606">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="text-center" data-id="element-1607">
            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} className="inline-flex items-center space-x-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-medium mb-8" data-id="element-1608">
              <SparklesIcon size={16} data-id="element-1609" />
              <span data-id="element-1610">Tu CFO virtual impulsado por IA</span>
            </motion.div>
            <h1 className="text-5xl md:text-7xl font-bold text-gray-900 mb-6" data-id="element-1611">
              Finanzas inteligentes
              <br data-id="element-1612" />
              <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent" data-id="element-1613">
                para empresas modernas
              </span>
            </h1>
            <p className="text-xl text-gray-600 mb-12 max-w-3xl mx-auto" data-id="element-1614">
              Toma decisiones estratégicas con reportes financieros en tiempo
              real, análisis predictivo y insights automáticos que impulsan tu
              crecimiento.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center" data-id="element-1615">
              <motion.button whileHover={{
              scale: 1.05
            }} whileTap={{
              scale: 0.95
            }} onClick={() => navigate('/register')} className="bg-blue-600 text-white px-8 py-4 rounded-lg text-lg font-medium hover:bg-blue-700 transition-colors flex items-center space-x-2 shadow-lg shadow-blue-600/30" data-id="element-1616">
                <span data-id="element-1617">Empieza gratis por 14 días</span>
                <ArrowRightIcon size={20} data-id="element-1618" />
              </motion.button>
              <motion.button whileHover={{
              scale: 1.05
            }} whileTap={{
              scale: 0.95
            }} className="border-2 border-gray-300 text-gray-700 px-8 py-4 rounded-lg text-lg font-medium hover:border-blue-600 hover:text-blue-600 transition-colors" data-id="element-1619">
                Ver demo
              </motion.button>
            </div>
            <p className="text-sm text-gray-500 mt-6" data-id="element-1620">
              No se requiere tarjeta de crédito • Configuración en 5 minutos •
              Cancela cuando quieras
            </p>
          </motion.div>
          {/* Hero Image/Dashboard Preview */}
          <motion.div initial={{
          opacity: 0,
          y: 40
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.3
        }} className="mt-20 relative" data-id="element-1621">
            <div className="absolute inset-0 bg-gradient-to-t from-white via-transparent to-transparent z-10" data-id="element-1622"></div>
            <div className="rounded-xl overflow-hidden shadow-2xl border border-gray-200" data-id="element-1623">
              <img src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1200&h=700&fit=crop" alt="FinancePro Dashboard" className="w-full" data-id="element-1624" />
            </div>
          </motion.div>
        </div>
      </section>
      {/* Features Section */}
      <section id="features" className="py-20 px-4 sm:px-6 lg:px-8" data-id="element-1625">
        <div className="max-w-7xl mx-auto" data-id="element-1626">
          <FadeInSection data-id="element-1627">
            <div className="text-center mb-16" data-id="element-1628">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4" data-id="element-1629">
                Todo lo que necesitas para
                <span className="text-blue-600" data-id="element-1630"> crecer</span>
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto" data-id="element-1631">
                Herramientas financieras de clase mundial en una plataforma
                intuitiva
              </p>
            </div>
          </FadeInSection>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-1632">
            {features.map((feature, index) => <FadeInSection key={index} delay={index * 0.1} data-id="element-1633">
                <motion.div whileHover={{
              y: -5
            }} className="bg-white p-8 rounded-xl border border-gray-200 hover:border-blue-300 hover:shadow-lg transition-all" data-id="element-1634">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600 mb-4" data-id="element-1635">
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-1636">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600" data-id="element-1637">{feature.description}</p>
                </motion.div>
              </FadeInSection>)}
          </div>
        </div>
      </section>
      {/* Benefits Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-white to-blue-50" data-id="element-1638">
        <div className="max-w-7xl mx-auto" data-id="element-1639">
          <FadeInSection data-id="element-1640">
            <div className="text-center mb-16" data-id="element-1641">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4" data-id="element-1642">
                Resultados que hablan por sí mismos
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto" data-id="element-1643">
                Únete a cientos de empresas que ya transformaron su gestión
                financiera
              </p>
            </div>
          </FadeInSection>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1644">
            {benefits.map((benefit, index) => <FadeInSection key={index} delay={index * 0.15} data-id="element-1645">
                <div className="bg-white p-8 rounded-xl shadow-lg text-center" data-id="element-1646">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-full flex items-center justify-center text-white mx-auto mb-6" data-id="element-1647">
                    {benefit.icon}
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3" data-id="element-1648">
                    {benefit.title}
                  </h3>
                  <p className="text-gray-600" data-id="element-1649">{benefit.description}</p>
                </div>
              </FadeInSection>)}
          </div>
        </div>
      </section>
      {/* Pricing Section */}
      <section id="pricing" className="py-20 px-4 sm:px-6 lg:px-8" data-id="element-1650">
        <div className="max-w-7xl mx-auto" data-id="element-1651">
          <FadeInSection data-id="element-1652">
            <div className="text-center mb-16" data-id="element-1653">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4" data-id="element-1654">
                Planes para cada etapa de tu empresa
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto" data-id="element-1655">
                Empieza gratis y escala cuando lo necesites
              </p>
            </div>
          </FadeInSection>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1656">
            {pricingPlans.map((plan, index) => <FadeInSection key={index} delay={index * 0.1} data-id="element-1657">
                <motion.div whileHover={{
              y: -10
            }} className={`bg-white p-8 rounded-xl border-2 ${plan.popular ? 'border-blue-600 shadow-xl shadow-blue-600/20' : 'border-gray-200'} relative`} data-id="element-1658">
                  {plan.popular && <div className="absolute -top-4 left-1/2 -translate-x-1/2" data-id="element-1659">
                      <span className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-4 py-1 rounded-full text-sm font-medium" data-id="element-1660">
                        Más popular
                      </span>
                    </div>}
                  <div className="text-center mb-8" data-id="element-1661">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-1662">
                      {plan.name}
                    </h3>
                    <p className="text-gray-600 mb-4" data-id="element-1663">{plan.description}</p>
                    <div className="flex items-baseline justify-center" data-id="element-1664">
                      {plan.price !== 'Personalizado' && <span className="text-gray-500 mr-1" data-id="element-1665">$</span>}
                      <span className="text-5xl font-bold text-gray-900" data-id="element-1666">
                        {plan.price}
                      </span>
                      {plan.price !== 'Personalizado' && <span className="text-gray-500 ml-2" data-id="element-1667">/mes</span>}
                    </div>
                  </div>
                  <ul className="space-y-4 mb-8" data-id="element-1668">
                    {plan.features.map((feature, fIndex) => <li key={fIndex} className="flex items-start space-x-3" data-id="element-1669">
                        <CheckCircleIcon size={20} className="text-green-600 flex-shrink-0 mt-0.5" data-id="element-1670" />
                        <span className="text-gray-700" data-id="element-1671">{feature}</span>
                      </li>)}
                  </ul>
                  <button onClick={() => navigate('/register')} className={`w-full py-3 rounded-lg font-medium transition-colors ${plan.popular ? 'bg-blue-600 text-white hover:bg-blue-700' : 'border-2 border-gray-300 text-gray-700 hover:border-blue-600 hover:text-blue-600'}`} data-id="element-1672">
                    {plan.cta}
                  </button>
                </motion.div>
              </FadeInSection>)}
          </div>
        </div>
      </section>
      {/* Testimonials Section */}
      <section id="testimonials" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-blue-50 to-white" data-id="element-1673">
        <div className="max-w-7xl mx-auto" data-id="element-1674">
          <FadeInSection data-id="element-1675">
            <div className="text-center mb-16" data-id="element-1676">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4" data-id="element-1677">
                Empresas que confían en nosotros
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto" data-id="element-1678">
                Descubre cómo FinancePro está transformando la gestión
                financiera
              </p>
            </div>
          </FadeInSection>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1679">
            {testimonials.map((testimonial, index) => <FadeInSection key={index} delay={index * 0.1} data-id="element-1680">
                <motion.div whileHover={{
              y: -5
            }} className="bg-white p-8 rounded-xl shadow-lg" data-id="element-1681">
                  <div className="flex items-center space-x-4 mb-6" data-id="element-1682">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center text-white font-medium" data-id="element-1683">
                      {testimonial.image}
                    </div>
                    <div data-id="element-1684">
                      <h4 className="font-semibold text-gray-900" data-id="element-1685">
                        {testimonial.name}
                      </h4>
                      <p className="text-sm text-gray-600" data-id="element-1686">
                        {testimonial.role}
                      </p>
                    </div>
                  </div>
                  <p className="text-gray-700 mb-4" data-id="element-1687">"{testimonial.content}"</p>
                  <p className="text-sm text-blue-600 font-medium" data-id="element-1688">
                    {testimonial.company}
                  </p>
                </motion.div>
              </FadeInSection>)}
          </div>
        </div>
      </section>
      {/* FAQ Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8" data-id="element-1689">
        <div className="max-w-4xl mx-auto" data-id="element-1690">
          <FadeInSection data-id="element-1691">
            <div className="text-center mb-16" data-id="element-1692">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4" data-id="element-1693">
                Preguntas frecuentes
              </h2>
              <p className="text-xl text-gray-600" data-id="element-1694">
                Todo lo que necesitas saber sobre FinancePro
              </p>
            </div>
          </FadeInSection>
          <div className="space-y-6" data-id="element-1695">
            {faqs.map((faq, index) => <FadeInSection key={index} delay={index * 0.05} data-id="element-1696">
                <FAQItem question={faq.question} answer={faq.answer} data-id="element-1697" />
              </FadeInSection>)}
          </div>
        </div>
      </section>
      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-600 to-indigo-600" data-id="element-1698">
        <div className="max-w-4xl mx-auto text-center" data-id="element-1699">
          <FadeInSection data-id="element-1700">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6" data-id="element-1701">
              Empieza a transformar tus finanzas hoy
            </h2>
            <p className="text-xl text-blue-100 mb-12" data-id="element-1702">
              Únete a cientos de empresas que ya están tomando mejores
              decisiones con FinancePro
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-1703">
              <motion.button whileHover={{
              scale: 1.05
            }} whileTap={{
              scale: 0.95
            }} onClick={() => navigate('/register')} className="bg-white text-blue-600 px-8 py-4 rounded-lg text-lg font-medium hover:bg-gray-100 transition-colors flex items-center justify-center space-x-2 shadow-xl" data-id="element-1704">
                <span data-id="element-1705">Prueba gratis por 14 días</span>
                <ArrowRightIcon size={20} data-id="element-1706" />
              </motion.button>
              <motion.button whileHover={{
              scale: 1.05
            }} whileTap={{
              scale: 0.95
            }} className="border-2 border-white text-white px-8 py-4 rounded-lg text-lg font-medium hover:bg-white hover:text-blue-600 transition-colors" data-id="element-1707">
                Hablar con ventas
              </motion.button>
            </div>
          </FadeInSection>
        </div>
      </section>
      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-12 px-4 sm:px-6 lg:px-8" data-id="element-1708">
        <div className="max-w-7xl mx-auto" data-id="element-1709">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8" data-id="element-1710">
            <div data-id="element-1711">
              <h3 className="text-white text-lg font-bold mb-4" data-id="element-1712">FinancePro</h3>
              <p className="text-sm" data-id="element-1713">
                Tu CFO virtual impulsado por IA para decisiones financieras más
                inteligentes.
              </p>
            </div>
            <div data-id="element-1714">
              <h4 className="text-white font-semibold mb-4" data-id="element-1715">Producto</h4>
              <ul className="space-y-2 text-sm" data-id="element-1716">
                <li data-id="element-1717">
                  <a href="#features" className="hover:text-white transition-colors" data-id="element-1718">
                    Características
                  </a>
                </li>
                <li data-id="element-1719">
                  <a href="#pricing" className="hover:text-white transition-colors" data-id="element-1720">
                    Precios
                  </a>
                </li>
                <li data-id="element-1721">
                  <a href="#" className="hover:text-white transition-colors" data-id="element-1722">
                    Integraciones
                  </a>
                </li>
                <li data-id="element-1723">
                  <a href="#" className="hover:text-white transition-colors" data-id="element-1724">
                    API
                  </a>
                </li>
              </ul>
            </div>
            <div data-id="element-1725">
              <h4 className="text-white font-semibold mb-4" data-id="element-1726">Empresa</h4>
              <ul className="space-y-2 text-sm" data-id="element-1727">
                <li data-id="element-1728">
                  <a href="#" className="hover:text-white transition-colors" data-id="element-1729">
                    Acerca de
                  </a>
                </li>
                <li data-id="element-1730">
                  <a href="#" className="hover:text-white transition-colors" data-id="element-1731">
                    Blog
                  </a>
                </li>
                <li data-id="element-1732">
                  <a href="#" className="hover:text-white transition-colors" data-id="element-1733">
                    Carreras
                  </a>
                </li>
                <li data-id="element-1734">
                  <a href="#" className="hover:text-white transition-colors" data-id="element-1735">
                    Contacto
                  </a>
                </li>
              </ul>
            </div>
            <div data-id="element-1736">
              <h4 className="text-white font-semibold mb-4" data-id="element-1737">Legal</h4>
              <ul className="space-y-2 text-sm" data-id="element-1738">
                <li data-id="element-1739">
                  <a href="#" className="hover:text-white transition-colors" data-id="element-1740">
                    Privacidad
                  </a>
                </li>
                <li data-id="element-1741">
                  <a href="#" className="hover:text-white transition-colors" data-id="element-1742">
                    Términos
                  </a>
                </li>
                <li data-id="element-1743">
                  <a href="#" className="hover:text-white transition-colors" data-id="element-1744">
                    Seguridad
                  </a>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center text-sm" data-id="element-1745">
            <p data-id="element-1746">© 2025 FinancePro. Todos los derechos reservados.</p>
          </div>
        </div>
      </footer>
    </div>;
};
// Utility component for fade-in animations
const FadeInSection: React.FC<{
  children: React.ReactNode;
  delay?: number;
}> = ({
  children,
  delay = 0
}) => {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  return <motion.div ref={ref} initial={{
    opacity: 0,
    y: 30
  }} animate={isInView ? {
    opacity: 1,
    y: 0
  } : {
    opacity: 0,
    y: 30
  }} transition={{
    duration: 0.6,
    delay
  }} data-id="element-1747">
      {children}
    </motion.div>;
};
// FAQ Item Component
const FAQItem: React.FC<{
  question: string;
  answer: string;
}> = ({
  question,
  answer
}) => {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-1748">
      <button onClick={() => setIsOpen(!isOpen)} className="w-full px-6 py-5 text-left flex justify-between items-center hover:bg-gray-50 transition-colors" data-id="element-1749">
        <span className="font-semibold text-gray-900" data-id="element-1750">{question}</span>
        <motion.div animate={{
        rotate: isOpen ? 180 : 0
      }} transition={{
        duration: 0.3
      }} data-id="element-1751">
          <svg className="w-5 h-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-1752">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" data-id="element-1753" />
          </svg>
        </motion.div>
      </button>
      <motion.div initial={false} animate={{
      height: isOpen ? 'auto' : 0
    }} transition={{
      duration: 0.3
    }} className="overflow-hidden" data-id="element-1754">
        <div className="px-6 pb-5 text-gray-600" data-id="element-1755">{answer}</div>
      </motion.div>
    </div>;
};
export default Landing;